import { SEO } from "../../hooks/useSEO";
import Hero from "../components/Hero";
import FeatureSection from "../components/FeatureSection";
import CTA from "../components/CTA";
import Footer from "../components/Footer";

const LANDING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Pingly",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web, iOS, Android, Chrome",
  "description": "AI-powered WhatsApp Business platform to automate conversations, send bulk broadcasts, build AI chatbots, and drive sales at scale.",
  "url": "https://thepingly.com",
  "screenshot": "https://thepingly.com/og-image.png",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free plan available"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Pingly",
    "url": "https://thepingly.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://thepingly.com/logo.svg"
    }
  }
};

const LandingPage = () => {
  return (
    <div className="antialiased text-slate-800 bg-[#FFFBF5]">
      <SEO 
        title="Pingly – AI-Powered WhatsApp Business Platform | Start Free" 
        description="Pingly is a WhatsApp Business API platform to automate conversations, send bulk broadcasts, build AI chatbots, and drive sales at scale. Try free today." 
        canonical="https://thepingly.com/"
        schemaJson={LANDING_SCHEMA}
      />
      <Hero />

      <div className="space-y-0">
        {/* Section 1: Broadcast Smarter */}
        <FeatureSection
          padding="pt-[88px] md:pt-[120px] pb-[100px]"
          bgClass="bg-[#FFFBF5]"
          pillText="Campaign Details"
          title="Broadcast Smarter. Reach Faster."
          description="Import contacts, send WhatsApp broadcasts at scale, and engage customers instantly – without spam or complexity."
          bulletPoints={[
            "Bulk contact import",
            "WhatsApp-approved broadcasts",
            "Fast, reliable delivery",
          ]}
          image={
            <img
              src="/broadcast-smarter.png"
              alt="Broadcast Smarter"
              className="w-full h-auto"
              width="720"
              height="480"
              loading="lazy"
            />
          }
        />

        {/* Section 2: Create WhatsApp Message Templates */}
        <FeatureSection
          padding="pt-[100px] pb-[0px]"
          bgClass="bg-white"
          reversed
          pillText="Templates"
          title="Create WhatsApp Message Templates with Ease"
          description="Design, submit, and manage WhatsApp-approved message templates – all from one simple dashboard. Send notifications, reminders, offers, and updates at scale without worrying about compliance."
          image={
            <img
              src="/create-whatsApp.png"
              alt="Create WhatsApp Message Templates"
              className="w-full h-auto"
              width="720"
              height="480"
              loading="lazy"
            />
          }
        />

        {/* Section 3: Broadcast Messages */}
        <FeatureSection
          padding="pt-[150px] pb-[50px]"
          bgClass="bg-[#FFFBF5]"
          pillText="Unlimited"
          title="Broadcast Messages to Unlimited Users - Officially"
          description="Send promotional and transactional WhatsApp messages at scale using the official WhatsApp Business API - with high delivery, zero risk of blocking, and full compliance."
          image={
            <img
              src="/broadcast-messages.png"
              alt="Broadcast Messages"
              className="w-full h-auto"
              width="720"
              height="480"
              loading="lazy"
            />
          }
          topDecoration={
            <svg
              width="1920"
              height="159"
              viewBox="0 0 1920 159"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto"
            >
              <path
                d="M1920 0.000579834C1920 0.000579834 1429.16 220.213 0 140.861V0L1920 0.000579834Z"
                fill="white"
              />
            </svg>
          }
        />

        {/* Section 4: Design Smart Chatbots */}
        <FeatureSection
          padding="pt-[10px] pb-[100px]"
          bgClass="bg-[#FFFBF5]"
          reversed
          pillText="Automations"
          title="Design Smart Chatbots - No Code Required"
          description="Create powerful WhatsApp chatbot experiences in minutes using Pingly's intuitive visual builder. Automate conversations, guide users, and scale engagement without technical complexity."
          bulletPoints={[
            "Visual drag-and-drop flow builder for quick setup",
            "Customize chatbot journeys with rules and conditions",
            "Deliver instant, accurate responses powered by AI",
            "Automate support, lead qualification, and sales 24/7",
          ]}
          image={
            <img
              src="/design-smart.png"
              alt="Design Smart Chatbots"
              className="w-full h-auto"
              width="720"
              height="480"
              loading="lazy"
            />
          }
        />

        {/* Section 5: Behavior-Driven Retargeting */}
        <FeatureSection
          padding="pt-[25px] pb-[75px]"
          bgClass="bg-white"
          pillText="Retargeting"
          title="Behavior-Driven Retargeting That Converts"
          description="Re-engage customers automatically based on how they interact with your messages, campaigns, and chatbots – and turn intent into action at the right moment."
          image={
            <img
              src="/behavior-driven.png"
              alt="Behavior-Driven Retargeting"
              className="w-full h-auto"
              width="720"
              height="480"
              loading="lazy"
            />
          }
        />
      </div>

      <CTA />
      <Footer />
    </div>
  );
};

export default LandingPage;
